// @ts-nocheck
'use client';

import React, { useState, useEffect } from 'react';
import { useWillStore } from '@/lib/store';
import { StepWrapper } from '../StepWrapper';
import { FormNavigation } from '../FormNavigation';
import { Gift, FileText, ChevronDown, ChevronUp, Pencil, Trash2, Sparkles, User, X, Check } from 'lucide-react';
import { ActionCard } from '@/app/components/ui/ActionCard';
import { Button } from '@/app/components/ui/Button';
import { Label } from '@/app/components/ui/Label';
import { Input } from '@/app/components/ui/Input';
import { Modal } from '@/app/components/ui/Modal';
import { AddressAutocomplete } from '../AddressAutocomplete';
import { useSavedPeople, SAVED_PEOPLE_PRESETS } from '@/lib/hooks/useSavedPeople';
import { AgeSelection } from '../shared/AgeSelection';
import { QuickAddPeople } from '../shared/QuickAddPeople';

export const Optionals = () => {
  const { data, setStep, setStepBack, setData, isEditingFromReview, setIsEditingFromReview, addManuallyAddedPerson, pendingGiftEditIndex, setPendingGiftEditIndex } = useWillStore();

  // Use shared hook to get saved people for quick add (include manual)
  const savedPeople = useSavedPeople({ ...SAVED_PEOPLE_PRESETS.optionals, includeManuallyAdded: true });

  // Section expansion state
  const [giftsExpanded, setGiftsExpanded] = useState(false);
  
  // Gift modal state
  const [giftModalOpen, setGiftModalOpen] = useState(false);
  const [editingGiftIndex, setEditingGiftIndex] = useState<number | null>(null);
  
  // Gift form state
  const [giftType, setGiftType] = useState<'item' | 'cash'>('item');
  const [giftDescription, setGiftDescription] = useState('');
  const [giftCashAmount, setGiftCashAmount] = useState('');
  const [giftRecipientName, setGiftRecipientName] = useState('');
  const [giftRecipientLocked, setGiftRecipientLocked] = useState(false);
  
  // Address State
  const [giftAddressLine1, setGiftAddressLine1] = useState('');
  const [giftAddressLine2, setGiftAddressLine2] = useState('');
  const [giftAddressCity, setGiftAddressCity] = useState('');
  const [giftAddressPostcode, setGiftAddressPostcode] = useState('');
  
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);

  // If coming from Review with a targeted gift, open the modal on mount/update
  useEffect(() => {
    if (pendingGiftEditIndex !== null && pendingGiftEditIndex !== undefined) {
      openEditGiftModal(pendingGiftEditIndex);
      setPendingGiftEditIndex(null);
    }
  }, [pendingGiftEditIndex]);

  const gifts = data.gifts || [];

  const resetGiftForm = () => {
    setGiftType('item');
    setGiftDescription('');
    setGiftCashAmount('');
    setGiftRecipientName('');
    setGiftRecipientLocked(false);
    setGiftAddressLine1('');
    setGiftAddressLine2('');
    setGiftAddressCity('');
    setGiftAddressPostcode('');
    setEditingGiftIndex(null);
  };

  const handleRecipientSave = (personData: { fullName: string; address: any }) => {
      const name = personData.fullName;
      setGiftRecipientName(name);
      setGiftRecipientLocked(true); // Treat as locked/selected
      setGiftAddressLine1(personData.address.line1 || '');
      setGiftAddressLine2(personData.address.line2 || '');
      setGiftAddressCity(personData.address.city || '');
      setGiftAddressPostcode(personData.address.postcode || '');

      // Track as manually added so it appears in quick add and people lists
      const manualList = data.manuallyAddedPeople || [];
      if (name && !manualList.some((p: any) => p.fullName === name)) {
        addManuallyAddedPerson({
          fullName: name,
          relationship: 'Recipient',
          address: personData.address
        });
      }

      setRecipientModalOpen(false);
  };

  const openAddGiftModal = () => {
    resetGiftForm();
    setGiftModalOpen(true);
  };

  const openEditGiftModal = (index: number) => {
    const gift = gifts[index];
    if (gift) {
      setGiftType(gift.giftType || 'item');
      setGiftDescription(gift.description || '');
      setGiftCashAmount(gift.cashAmount?.toString() || '');
      setGiftRecipientName((gift.recipient as any)?.fullName || '');
      setGiftRecipientLocked(false); // Default to unlocked when editing? Or maybe locked if it matches a saved person? Let's leave unlocked for flexibility.
      
      const addr = (gift.recipient as any)?.address;
      setGiftAddressLine1(addr?.line1 || '');
      setGiftAddressLine2(addr?.line2 || '');
      setGiftAddressCity(addr?.city || '');
      setGiftAddressPostcode(addr?.postcode || '');
      
      setEditingGiftIndex(index);
      setGiftModalOpen(true);
    }
  };

  const saveGift = () => {
    const newGift = {
      type: 'individual' as const,
      giftType,
      description: giftType === 'item' ? giftDescription : undefined,
      cashAmount: giftType === 'cash' ? Number(giftCashAmount) : undefined,
      recipient: {
        fullName: giftRecipientName,
        address: { 
            line1: giftAddressLine1, 
            line2: giftAddressLine2, 
            city: giftAddressCity, 
            postcode: giftAddressPostcode 
        }
      },
      recipientFailPolicy: 'fail' as const,
    };

    let updatedGifts;
    if (editingGiftIndex !== null) {
      updatedGifts = [...gifts];
      updatedGifts[editingGiftIndex] = newGift;
    } else {
      updatedGifts = [...gifts, newGift];
    }
    
    setData({ gifts: updatedGifts });
    setGiftModalOpen(false);
    resetGiftForm();
  };

  const removeGift = (index: number) => {
    const updatedGifts = gifts.filter((_: any, i: number) => i !== index);
    setData({ gifts: updatedGifts });
  };

  const handleNext = () => {
    
    if (isEditingFromReview) {
        setIsEditingFromReview(false);
        setStep(5); // Go to Review
    } else {
        setStep(5); // Go to Review
    }
  };

  const handleBack = () => {
    setStepBack(3); // Go back to last page of Executors
  };

  return (
    <StepWrapper
      title="Specific Gifts"
      description={
        <>
          This section is optional. Use it to leave specific items or cash to individuals.
          <ul className="list-disc list-inside mt-2 space-y-1 text-muted-foreground">
            <li>A sentimental item like jewellery, a watch, or a family heirloom</li>
            <li>A specific cash amount (e.g., £1,000 to a godchild)</li>
            <li>Personal possessions like a car, artwork, or musical instrument</li>
          </ul>
          <p className="text-xs text-muted-foreground/70 mt-2 italic">Note: These gifts are distributed first, before the remainder of your estate goes to your beneficiaries.</p>
        </>
      }
    >
      <div className="w-full md:w-1/2 space-y-6">
        {gifts.length > 0 && (
          <div className="grid gap-3">
            {gifts.map((gift: any, index: number) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-primary/20 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="bg-white p-2 rounded-lg border border-gray-100 text-gray-400">
                    {gift.giftType === 'cash' ? <span className="font-bold text-lg text-emerald-600">£</span> : <Gift className="h-5 w-5" />}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {gift.giftType === 'cash' ? `£${gift.cashAmount?.toLocaleString('en-GB')}` : gift.description}
                    </p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <span className="text-gray-400">to</span> {gift.recipient?.fullName}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={() => openEditGiftModal(index)} className="hover:bg-white hover:text-primary">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => removeGift(index)} className="hover:bg-white hover:text-destructive text-destructive/70">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <ActionCard showPlusIcon onClick={openAddGiftModal} className="w-full">Add Gift</ActionCard>
      </div>

      <FormNavigation onBack={handleBack} onNext={handleNext} />

      {/* Gift Modal */}
      <Modal isOpen={giftModalOpen} onClose={() => { setGiftModalOpen(false); resetGiftForm(); }} title={editingGiftIndex !== null ? 'Edit Gift' : 'Add a Gift'}>
        <div className="space-y-4">
          <div className="space-y-6">
            <Label className="text-base font-medium text-gray-700">What type of gift?</Label>
            <div className="flex gap-4 staggered-fade-in">
              <ActionCard selected={giftType === 'item'} onClick={() => setGiftType('item')}>Item</ActionCard>
              <ActionCard selected={giftType === 'cash'} onClick={() => setGiftType('cash')}>Cash</ActionCard>
            </div>
          </div>

          {giftType === 'item' ? (
            <div className="space-y-2">
              <Label className="text-base font-medium text-gray-700">Description of the item</Label>
              <Input
                value={giftDescription}
                onChange={(e) => setGiftDescription(e.target.value)}
                placeholder="e.g. My gold watch, My piano, etc."
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-base font-medium text-gray-700">Cash Amount (£)</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={giftCashAmount ? Number(giftCashAmount).toLocaleString('en-GB') : ''}
                onChange={(e) => {
                  // Only allow digits
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  setGiftCashAmount(raw);
                }}
                placeholder="e.g. 1,000"
              />
            </div>
          )}

          <div className="space-y-3">
            <Label className="text-base font-medium text-gray-700">Recipient</Label>

            {/* Display Selected Recipient */}
            {giftRecipientName ? (
                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 flex justify-between items-start animate-fade-in group relative">
                    <div className="text-sm text-gray-700">
                        <p className="font-semibold text-base mb-1">{giftRecipientName}</p>
                        {giftAddressLine1 ? (
                            <>
                                <p>{giftAddressLine1}</p>
                                {giftAddressLine2 && <p>{giftAddressLine2}</p>}
                                <p>{giftAddressCity}, {giftAddressPostcode}</p>
                            </>
                        ) : (
                             <p className="text-muted-foreground italic">No address provided</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            setGiftRecipientName('');
                            setGiftRecipientLocked(false);
                            setGiftAddressLine1('');
                            setGiftAddressLine2('');
                            setGiftAddressCity('');
                            setGiftAddressPostcode('');
                        }}
                        className="text-gray-400 hover:text-destructive transition-colors absolute top-2 right-2 p-1"
                        title="Remove recipient"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
            ) : (
                // Selection Options
                <div className="space-y-4 animate-fade-in">
                    {savedPeople.length > 0 && (
                      <QuickAddPeople
                        heading="Quick add recipient"
                        people={savedPeople}
                        selectedPeople={giftRecipientName ? [{ fullName: giftRecipientName } as any] : []}
                        isSelected={(name) => giftRecipientName === name}
                        onToggle={(person) => {
                          setGiftRecipientName(person.fullName);
                          setGiftRecipientLocked(true);
                          if (person.address) {
                            setGiftAddressLine1(person.address.line1 || '');
                            setGiftAddressLine2(person.address.line2 || '');
                            setGiftAddressCity(person.address.city || '');
                            setGiftAddressPostcode(person.address.postcode || '');
                          }
                        }}
                      />
                    )}

                    <ActionCard showPlusIcon onClick={() => setRecipientModalOpen(true)} className="w-full">Add Recipient</ActionCard>
                </div>
            )}
          </div>

          <div className="pt-4">
            <Button 
              onClick={saveGift}
              disabled={!giftRecipientName || (giftType === 'item' ? !giftDescription : !giftCashAmount)}
              className="w-full"
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add Recipient Modal */}
      <Modal isOpen={recipientModalOpen} onClose={() => setRecipientModalOpen(false)} title="Add Recipient">
        <AddRecipientForm 
            onSave={handleRecipientSave}
        />
      </Modal>
    </StepWrapper>
  );
};

const AddRecipientForm = ({ onSave }: { onSave: (data: any) => void }) => {
    const [fullName, setFullName] = useState('');
    const [isOver18, setIsOver18] = useState<boolean | undefined>(undefined);
    const [line1, setLine1] = useState('');
    const [line2, setLine2] = useState('');
    const [city, setCity] = useState('');
    const [postcode, setPostcode] = useState('');
    const [needsLine1, setNeedsLine1] = useState(false);
    
    // Modes: 'search' | 'summary' | 'edit'
    // 'search' = initial state, shows autocomplete + manual link
    // 'summary' = address selected/filled, shows box
    // 'edit' = manual entry mode, shows inputs
    const [mode, setMode] = useState<'search' | 'summary' | 'edit'>('search');

    const isValid = fullName.trim().indexOf(' ') !== -1 && isOver18 !== undefined && !!line1 && !!city && !!postcode;

    const handleAddressSelect = (addr: any) => {
        setLine1(addr.line1);
        setLine2(addr.line2);
        setCity(addr.city);
        setPostcode(addr.postcode);
        setNeedsLine1(false);
        setMode('summary');
    };

    const handleIncompleteAddress = (addr: any) => {
        setLine1('');
        setLine2(addr.line2 || '');
        setCity(addr.city || '');
        setPostcode(addr.postcode || '');
        setNeedsLine1(true);
        setMode('edit');
    };

    const handleClear = () => {
        setLine1('');
        setLine2('');
        setCity('');
        setPostcode('');
        setNeedsLine1(false);
        setMode('search');
    };

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <Label className="text-base font-medium text-gray-700">Full Name</Label>
                <Input 
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder=""
                />
            </div>

            <div className="space-y-3">
                <Label className="text-base font-medium text-gray-700">Address</Label>
                
                {/* Search Mode */}
                {mode === 'search' && (
                    <div className="space-y-2">
                        <AddressAutocomplete
                            onAddressSelect={handleAddressSelect}
                            onIncompleteAddress={handleIncompleteAddress}
                        />
                        <button 
                            type="button"
                            onClick={() => setMode('edit')}
                            className="text-sm text-primary font-medium hover:underline mt-2"
                        >
                            Enter address manually
                        </button>
                    </div>
                )}

                {/* Summary Mode */}
                {mode === 'summary' && (
                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 flex justify-between items-start animate-fade-in group">
                        <div className="text-sm text-gray-700">
                            <p className="font-medium">{line1}</p>
                            {line2 && <p>{line2}</p>}
                            <p>{city}</p>
                            <p>{postcode}</p>
                        </div>
                        <div className="flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={() => setMode('edit')}
                                className="text-xs font-medium text-primary hover:text-primary/80 border border-primary/20 bg-white hover:bg-primary/5 px-3 py-1.5 rounded transition-colors"
                            >
                                Edit
                            </button>
                            <button
                                type="button"
                                onClick={handleClear}
                                className="text-xs text-gray-400 hover:text-destructive transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                )}
                
                {/* Edit Mode */}
                {mode === 'edit' && (
                    <div className="space-y-3 animate-fade-in p-4 border border-gray-200 rounded-lg bg-white relative">
                        <Input 
                            value={line1}
                            onChange={(e) => {
                                setLine1(e.target.value);
                                if (e.target.value) setNeedsLine1(false);
                            }}
                            placeholder="Address Line 1"
                        />
                        {needsLine1 && <p className="text-destructive text-sm">Please enter Address Line 1.</p>}
                        <Input 
                            value={line2}
                            onChange={(e) => setLine2(e.target.value)}
                            placeholder="Address Line 2 (Optional)"
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <Input 
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                placeholder="City"
                            />
                            <Input 
                                value={postcode}
                                onChange={(e) => setPostcode(e.target.value)}
                                placeholder="Postcode"
                            />
                        </div>

                        {(line1 || city || postcode) && (
                            <div className="pt-2 flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!line1) {
                                            setNeedsLine1(true);
                                            return;
                                        }
                                        setMode('summary');
                                    }}
                                    className="text-xs font-medium text-primary hover:underline"
                                >
                                    Done
                                </button>
                            </div>
                        )}
                        
                         {!line1 && !city && !postcode && (
                             <div className="pt-2 flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    className="text-xs text-gray-400 hover:text-destructive transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <AgeSelection 
                value={isOver18}
                onChange={setIsOver18}
                name="recipient-age"
            />

            <div className="pt-4">
                <Button 
                    onClick={() => onSave({ 
                        fullName,
                        isOver18,
                        address: { line1, line2, city, postcode } 
                    })}
                    disabled={!isValid}
                    className="w-full"
                >
                    Save
                </Button>
            </div>
        </div>
    );
};

